import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { MessageSquare, Sparkles, Bot, User } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { API_BASE_URL } from '../config';
import { useAuth } from '../contexts/AuthContext';
import { decodeHtmlEntities } from '../utils/decodeHtmlEntities';

const productDisplayText = (value, fallback = '') => {
  const raw = String(value ?? '').trim();
  if (!raw) return fallback;
  return decodeHtmlEntities(raw);
};

const INITIAL_VISIBLE_PRODUCTS = 12;
const CHAT_FETCH_TOP_K = 200;
const LOADING_STATUS_MESSAGES = [
  'Understanding your request...',
  'Searching the product catalog...',
  'Ranking and refining matches...',
];

const AgenticWorkingIndicator = ({ message }) => (
  <div className="d-flex justify-content-start" role="status" aria-live="polite" aria-busy="true">
    <div className="rounded-3 px-3 py-2 bg-light text-dark border agentic-working-bubble" style={{ maxWidth: '90%' }}>
      <div className="small d-flex align-items-center gap-2 mb-1">
        <span className="spinner-border spinner-border-sm text-secondary" aria-hidden="true" />
        <Bot size={13} aria-hidden="true" />
        <span className="fw-semibold">Assistant</span>
      </div>
      <div className="small text-muted mb-0 agentic-working-message">{message}</div>
    </div>
  </div>
);

const makeWsUrl = (baseUrl, path, apiKey) => {
  const normalized = String(baseUrl || '').replace(/^http/i, 'ws').replace(/\/$/, '');
  const token = encodeURIComponent(String(apiKey || '').trim());
  return `${normalized}${path}?x_api_key=${token}`;
};

const ProductCard = ({ product, onAddToCart }) => {
  const attrs = Array.isArray(product.attributes) ? product.attributes.slice(0, 4) : [];
  const imageUrl = String(product.image || '').trim();
  const productTitle = productDisplayText(product.short_desc || product.part_number, 'Product');
  const brand = productDisplayText(product.manufacturer, '-');
  const category = productDisplayText(product.category, '-');
  return (
    <div className="col-12 col-sm-6 col-lg-4 col-xxl-3 d-flex">
      <article className="card border-0 shadow-sm h-100 w-100 ecom-product-card">
        <div className="ecom-product-image-wrap">
          {imageUrl ? (
            <img
              src={imageUrl}
              alt={`${productTitle} image`}
              className="ecom-product-image"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
                const fallback = e.currentTarget.parentElement?.querySelector('.ecom-product-image-fallback');
                if (fallback) fallback.classList.remove('d-none');
              }}
            />
          ) : null}
          <div className={`ecom-product-image-fallback ${imageUrl ? 'd-none' : ''}`}>
            <span>No Image</span>
          </div>
          <span className="badge text-bg-light ecom-score-badge">Score {Number(product.score || 0).toFixed(2)}</span>
        </div>
        <div className="card-body d-flex flex-column">
          <h6 className="mb-2 ecom-product-title" title={productTitle}>
            {productTitle}
          </h6>
          <div className="small text-muted mb-1">Part #: <span className="fw-semibold text-dark">{product.part_number || '-'}</span></div>
          <div className="small text-muted mb-1">Brand: <span className="fw-semibold text-dark">{brand}</span></div>
          <div className="small text-muted mb-3">Category: <span className="fw-semibold text-dark">{category}</span></div>
          <div className="d-flex flex-wrap gap-1 mb-3">
            {attrs.length > 0 ? (
              attrs.map((a, idx) => (
                <span key={`${a.name}-${idx}`} className="badge text-bg-light border ecom-attr-pill">
                  {productDisplayText(a.name)}: {productDisplayText(a.value)}
                </span>
              ))
            ) : (
              <span className="small text-muted">No attributes available</span>
            )}
          </div>
          <div className="mt-auto d-flex gap-2">
            <button type="button" className="btn btn-sm btn-outline-dark flex-fill">Quick View</button>
            <button type="button" className="btn btn-sm btn-dark flex-fill" onClick={() => onAddToCart?.(product)}>
              Add to Cart
            </button>
          </div>
        </div>
      </article>
    </div>
  );
};

const renderFormattedMessage = (text) => {
  const raw = String(text || '');
  const blocks = raw.split(/\n\s*\n/).map((b) => b.trim()).filter(Boolean);
  if (blocks.length === 0) {
    return <span>{raw}</span>;
  }

  return (
    <div className="formatted-chat-message d-flex flex-column gap-2">
      {blocks.map((block, blockIdx) => {
        const lines = block.split('\n').map((line) => line.trim()).filter(Boolean);
        const allBullets = lines.length > 0 && lines.every((line) => /^[-*]\s+/.test(line));
        if (allBullets) {
          return (
            <ul key={`b-${blockIdx}`} className="mb-0 ps-3">
              {lines.map((line, lineIdx) => (
                <li key={`bl-${blockIdx}-${lineIdx}`}>{line.replace(/^[-*]\s+/, '')}</li>
              ))}
            </ul>
          );
        }

        return (
          <p key={`p-${blockIdx}`} className="mb-0">
            {block.split(/(\b[A-Za-z][A-Za-z0-9\s]+:\s)/g).map((segment, idx) => {
              if (/^[A-Za-z][A-Za-z0-9\s]+:\s$/.test(segment)) {
                return (
                  <strong key={`s-${blockIdx}-${idx}`} className="me-1">
                    {segment}
                  </strong>
                );
              }
              return <span key={`t-${blockIdx}-${idx}`}>{segment}</span>;
            })}
          </p>
        );
      })}
    </div>
  );
};

const AgenticShop = () => {
  const { getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [sessionId] = useState(() => `agentic-${Date.now().toString(36)}`);
  const [input, setInput] = useState('');
  const [chatHistory, setChatHistory] = useState([]);
  const [products, setProducts] = useState([]);
  const [filtersApplied, setFiltersApplied] = useState({});
  const [followUps, setFollowUps] = useState([]);
  const [totalResults, setTotalResults] = useState(0);
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE_PRODUCTS);
  const [facetOptions, setFacetOptions] = useState([]);
  const [facetContext, setFacetContext] = useState({ query: '', filters: {} });
  const [secondPassApplied, setSecondPassApplied] = useState(false);
  const [secondPassFilters, setSecondPassFilters] = useState({});
  const [comparison, setComparison] = useState(null);
  const [partialTranscript, setPartialTranscript] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingStatus, setLoadingStatus] = useState(LOADING_STATUS_MESSAGES[0]);
  const [isRecording, setIsRecording] = useState(false);
  const memoryReady = null;
  const [cartItems, setCartItems] = useState([]);
  const [secondPassEngine, setSecondPassEngine] = useState('inbuilt');
  const [aiEngineConfigured, setAiEngineConfigured] = useState(false);
  const [engineSaving, setEngineSaving] = useState(false);
  const [agentSetup, setAgentSetup] = useState({});
  const [agentSetupSaving, setAgentSetupSaving] = useState(false);
  const [agentSetupOpen, setAgentSetupOpen] = useState(false);
  const [connectorStatus, setConnectorStatus] = useState([]);
  const [connectorStatusLoading, setConnectorStatusLoading] = useState(false);
  const [externalMcpNote, setExternalMcpNote] = useState('');
  const [mcpServers, setMcpServers] = useState([]);
  const [mcpBusyById, setMcpBusyById] = useState({});
  const [mcpSaving, setMcpSaving] = useState(false);
  const [mcpPanelOpen, setMcpPanelOpen] = useState(false);
  const [mcpForm, setMcpForm] = useState({
    id: '',
    name: '',
    install_method: 'npm_global',
    package_name: '',
    command: '',
    args_text: '',
    env_text: '',
    enabled: true,
  });

  const wsRef = useRef(null);
  const streamRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const recordedChunksRef = useRef([]);
  const chatBottomRef = useRef(null);
  const chatScrollRef = useRef(null);

  const hasApiKey = useMemo(() => {
    const headers = getAuthHeaders({ useBearer: false }) || {};
    return Boolean(headers['X-API-Key']);
  }, [getAuthHeaders]);

  const sendChat = async (message, clientContext = null) => {
    const text = String(message || '').trim();
    if (!text) return;
    setLoading(true);
    setChatHistory((prev) => [...prev, { role: 'user', text }]);
    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/agentic-shop/chat`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          session_id: sessionId,
          message: text,
          top_k: CHAT_FETCH_TOP_K,
          search_mode: 'semantic',
          client_context: {
            ...(clientContext && typeof clientContext === 'object' ? clientContext : {}),
            second_pass_engine: secondPassEngine,
          },
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data?.detail || `Chat failed (${response.status})`);
      setChatHistory((prev) => [
        ...prev,
        {
          role: 'assistant',
          text: data.response || 'No response',
          products: Array.isArray(data.products) ? data.products.slice(0, 3) : [],
          followUps: Array.isArray(data.follow_up_questions) ? data.follow_up_questions : [],
          intent: String(data.intent || ''),
          agentCalled: String(data.agent_called || ''),
          agentsRequired: Array.isArray(data.agents_required) ? data.agents_required : [],
          agentMissing: Array.isArray(data.agent_missing) ? data.agent_missing : [],
          facetOptions: Array.isArray(data.facet_options) ? data.facet_options : [],
          contextQuery: String(data.context_query || ''),
          contextFilters: data.context_filters && typeof data.context_filters === 'object' ? data.context_filters : {},
          secondPassApplied: Boolean(data.second_pass_applied),
          secondPassFilters: data.second_pass_filters && typeof data.second_pass_filters === 'object' ? data.second_pass_filters : {},
        },
      ]);
      setProducts(Array.isArray(data.products) ? data.products : []);
      const nextProducts = Array.isArray(data.products) ? data.products : [];
      const nextTotal = Number(data?.total_results || data?.result_count || nextProducts.length || 0);
      setTotalResults(nextTotal);
      setVisibleCount(Math.max(1, Math.min(INITIAL_VISIBLE_PRODUCTS, nextProducts.length || INITIAL_VISIBLE_PRODUCTS)));
      setFiltersApplied(data.filters_applied && typeof data.filters_applied === 'object' ? data.filters_applied : {});
      setFollowUps(Array.isArray(data.follow_up_questions) ? data.follow_up_questions : []);
      setFacetOptions(Array.isArray(data.facet_options) ? data.facet_options : []);
      setFacetContext({
        query: String(data.context_query || ''),
        filters: data.context_filters && typeof data.context_filters === 'object' ? data.context_filters : {},
      });
      setSecondPassApplied(Boolean(data.second_pass_applied));
      setSecondPassFilters(data.second_pass_filters && typeof data.second_pass_filters === 'object' ? data.second_pass_filters : {});
      setComparison(data?.comparison && typeof data.comparison === 'object' ? data.comparison : null);
      setInput('');
    } catch (error) {
      toast.error(String(error?.message || error));
    } finally {
      setLoading(false);
    }
  };

  const handleFollowUpClick = (question, context = {}) => {
    if (loading) return;
    const q = String(question || '').trim();
    if (!q) return;
    // Keep follow-up text clean; context is already preserved server-side via session memory.
    setInput(q);
    sendChat(q);
  };

  const handleFacetOptionClick = (option, messageContext = {}) => {
    if (loading) return;
    const opt = option && typeof option === 'object' ? option : {};
    const filterKey = String(opt.filter_key || '').trim();
    const value = String(opt.value || '').trim();
    if (!filterKey || !value) return;
    const fallbackQuery = String(facetContext?.query || '').trim();
    const fallbackFilters = facetContext?.filters && typeof facetContext.filters === 'object'
      ? facetContext.filters
      : {};
    const baseQuery = String(messageContext?.contextQuery || fallbackQuery).trim();
    const baseFilters = messageContext?.contextFilters && typeof messageContext.contextFilters === 'object'
      ? messageContext.contextFilters
      : fallbackFilters;
    const existing = String(baseFilters?.[filterKey] || '').trim().toLowerCase();
    if (existing && existing === value.toLowerCase()) {
      toast('Filter already applied');
      return;
    }
    const message = String(opt.prompt || `Filter by ${filterKey} ${value}`).trim();
    setInput(message);
    sendChat(message, {
      base_query: baseQuery,
      base_filters: baseFilters,
      apply_filter: {
        key: filterKey,
        value,
      },
      source: 'facet_option',
      preserve_context: true,
    });
  };

  const removeSingleFilter = (filterKey) => {
    if (loading) return;
    const key = String(filterKey || '').trim();
    if (!key) return;
    const nextFilters = { ...(facetContext?.filters || {}) };
    if (!(key in nextFilters)) return;
    delete nextFilters[key];
    const baseQuery = String(facetContext?.query || '').trim() || String(input || '').trim();
    const message = `Remove filter ${key}`;
    setInput(message);
    sendChat(message, {
      base_query: baseQuery,
      base_filters: nextFilters,
      preserve_context: true,
      remove_filter: key,
      source: 'remove_filter',
    });
  };

  const clearAllFilters = () => {
    if (loading) return;
    const baseQuery = String(facetContext?.query || '').trim() || String(input || '').trim();
    if (!baseQuery) return;
    const message = 'Clear all filters';
    setInput(message);
    sendChat(message, {
      base_query: baseQuery,
      base_filters: {},
      preserve_context: true,
      clear_all_filters: true,
      source: 'clear_filters',
    });
  };

  const addToCart = (product) => {
    const key = String(product?.item_id || product?.part_number || '').trim();
    if (!key) {
      toast.error('Unable to add product to cart.');
      return;
    }
    setCartItems((prev) => {
      const exists = prev.some((item) => item.key === key);
      if (exists) {
        toast('Already in cart');
        return prev;
      }
      const next = [
        ...prev,
        {
          key,
          title: productDisplayText(product.short_desc || product.part_number, 'Product'),
          partNumber: String(product.part_number || ''),
        },
      ];
      toast.success('Added to cart');
      return next;
    });
  };

  const loadSecondPassSettings = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/agentic-shop/settings`, {
        method: 'GET',
        headers: getAuthHeaders(),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data?.detail || `Settings load failed (${response.status})`);
      const engine = String(data?.second_pass_engine || 'inbuilt').trim().toLowerCase();
      setSecondPassEngine(engine === 'ai' ? 'ai' : 'inbuilt');
      setAiEngineConfigured(Boolean(data?.ai_engine_configured));
    } catch (error) {
      toast.error(String(error?.message || error));
    }
  };

  const updateSecondPassEngine = async (engine) => {
    const nextEngine = String(engine || '').trim().toLowerCase();
    if (!['inbuilt', 'ai'].includes(nextEngine)) return;
    setSecondPassEngine(nextEngine);
    setEngineSaving(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/agentic-shop/settings`, {
        method: 'PUT',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ second_pass_engine: nextEngine }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data?.detail || `Settings update failed (${response.status})`);
      toast.success(`Second pass engine set to ${nextEngine === 'ai' ? 'AI' : 'Inbuilt'}`);
    } catch (error) {
      toast.error(String(error?.message || error));
      setSecondPassEngine((prev) => (prev === 'ai' ? 'inbuilt' : 'ai'));
    } finally {
      setEngineSaving(false);
    }
  };

  const loadAgentSetup = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/agentic-shop/agents`, {
        method: 'GET',
        headers: getAuthHeaders(),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data?.detail || `Agent setup load failed (${response.status})`);
      const agents = data?.agents && typeof data.agents === 'object' ? data.agents : {};
      setAgentSetup(agents);
    } catch (error) {
      toast.error(String(error?.message || error));
    }
  };

  const saveAgentSetup = async () => {
    setAgentSetupSaving(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/agentic-shop/agents`, {
        method: 'PUT',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ agents: agentSetup }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data?.detail || `Agent setup update failed (${response.status})`);
      setAgentSetup(data?.agents && typeof data.agents === 'object' ? data.agents : agentSetup);
      toast.success('Agent orchestration setup saved');
    } catch (error) {
      toast.error(String(error?.message || error));
    } finally {
      setAgentSetupSaving(false);
    }
  };

  const toggleAgent = (agentName, enabled) => {
    const name = String(agentName || '').trim();
    if (!name) return;
    setAgentSetup((prev) => ({ ...prev, [name]: Boolean(enabled) }));
  };

  const loadConnectorStatus = async () => {
    setConnectorStatusLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/agentic-shop/mcp-status`, {
        method: 'GET',
        headers: getAuthHeaders(),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data?.detail || `Connector status failed (${response.status})`);
      setConnectorStatus(Array.isArray(data?.connectors) ? data.connectors : []);
      setExternalMcpNote(String(data?.external_mcp_note || '').trim());
    } catch (error) {
      toast.error(String(error?.message || error));
      setConnectorStatus([]);
      setExternalMcpNote('');
    } finally {
      setConnectorStatusLoading(false);
    }
  };

  const loadMcpServers = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/agentic-shop/mcp-servers`, {
        method: 'GET',
        headers: getAuthHeaders(),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data?.detail || `MCP servers load failed (${response.status})`);
      setMcpServers(Array.isArray(data?.servers) ? data.servers : []);
    } catch (error) {
      toast.error(String(error?.message || error));
      setMcpServers([]);
    }
  };

  const installMcpServer = async (serverId) => {
    const key = String(serverId || '').trim();
    if (!key) return;
    setMcpBusyById((prev) => ({ ...prev, [`install:${key}`]: true }));
    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/agentic-shop/mcp-servers/${encodeURIComponent(key)}/install`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data?.detail || `Install failed (${response.status})`);
      toast.success(data?.ok ? 'MCP server installed' : 'Install completed with errors');
      await loadMcpServers();
    } catch (error) {
      toast.error(String(error?.message || error));
    } finally {
      setMcpBusyById((prev) => ({ ...prev, [`install:${key}`]: false }));
    }
  };

  const testMcpServer = async (serverId) => {
    const key = String(serverId || '').trim();
    if (!key) return;
    setMcpBusyById((prev) => ({ ...prev, [`test:${key}`]: true }));
    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/agentic-shop/mcp-servers/${encodeURIComponent(key)}/test`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data?.detail || `Test failed (${response.status})`);
      toast.success(data?.ok ? 'MCP server test passed' : 'MCP server test failed');
      await loadMcpServers();
    } catch (error) {
      toast.error(String(error?.message || error));
    } finally {
      setMcpBusyById((prev) => ({ ...prev, [`test:${key}`]: false }));
    }
  };

  const deleteMcpServer = async (serverId) => {
    const key = String(serverId || '').trim();
    if (!key) return;
    setMcpBusyById((prev) => ({ ...prev, [`delete:${key}`]: true }));
    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/agentic-shop/mcp-servers/${encodeURIComponent(key)}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data?.detail || `Delete failed (${response.status})`);
      toast.success('MCP server removed');
      await loadMcpServers();
    } catch (error) {
      toast.error(String(error?.message || error));
    } finally {
      setMcpBusyById((prev) => ({ ...prev, [`delete:${key}`]: false }));
    }
  };

  const editMcpServer = (server) => {
    const next = server && typeof server === 'object' ? server : {};
    setMcpPanelOpen(true);
    setMcpForm({
      id: String(next.id || ''),
      name: String(next.name || ''),
      install_method: String(next.install_method || 'npm_global'),
      package_name: String(next.package_name || ''),
      command: String(next.command || ''),
      args_text: Array.isArray(next.args) ? next.args.join(' ') : '',
      env_text: next.env && typeof next.env === 'object' ? JSON.stringify(next.env, null, 2) : '',
      enabled: Boolean(next.enabled),
    });
  };

  const clearMcpForm = () => {
    setMcpForm({
      id: '',
      name: '',
      install_method: 'npm_global',
      package_name: '',
      command: '',
      args_text: '',
      env_text: '',
      enabled: true,
    });
  };

  const saveMcpServer = async () => {
    const name = String(mcpForm.name || '').trim();
    const packageName = String(mcpForm.package_name || '').trim();
    const command = String(mcpForm.command || '').trim();
    if (!name || !packageName || !command) {
      toast.error('Name, package, and command are required');
      return;
    }
    let parsedEnv = {};
    const envText = String(mcpForm.env_text || '').trim();
    if (envText) {
      try {
        const raw = JSON.parse(envText);
        if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
          parsedEnv = raw;
        } else {
          throw new Error('Env must be a JSON object');
        }
      } catch (error) {
        toast.error(`Invalid env JSON: ${String(error?.message || error)}`);
        return;
      }
    }
    const args = String(mcpForm.args_text || '')
      .split(/\s+/)
      .map((v) => v.trim())
      .filter(Boolean);
    setMcpSaving(true);
    try {
      const payload = {
        id: String(mcpForm.id || '').trim() || undefined,
        name,
        install_method: String(mcpForm.install_method || 'npm_global'),
        package_name: packageName,
        command,
        args,
        env: parsedEnv,
        enabled: Boolean(mcpForm.enabled),
      };
      const response = await fetch(`${API_BASE_URL}/api/v1/agentic-shop/mcp-servers`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data?.detail || `Save MCP server failed (${response.status})`);
      toast.success('MCP server configuration saved');
      clearMcpForm();
      await loadMcpServers();
    } catch (error) {
      toast.error(String(error?.message || error));
    } finally {
      setMcpSaving(false);
    }
  };

  const transcribeWithWhisper = async () => {
    const chunks = recordedChunksRef.current || [];
    if (!chunks.length) return;
    const blob = new Blob(chunks, { type: 'audio/webm' });
    const formData = new FormData();
    formData.append('file', blob, 'agentic-voice.webm');

    const authHeaders = getAuthHeaders() || {};
    const uploadHeaders = { ...authHeaders };
    delete uploadHeaders['Content-Type'];

    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/agentic-shop/voice/transcribe`, {
        method: 'POST',
        headers: uploadHeaders,
        body: formData,
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data?.detail || `Whisper failed (${response.status})`);
      const transcript = String(data?.transcript || '').trim();
      if (transcript) {
        setInput(transcript);
        setPartialTranscript('');
      }
    } catch (error) {
      toast.error(String(error?.message || error));
    }
  };

  const startVoice = async () => {
    if (!hasApiKey) {
      toast.error('Voice streaming requires API key authentication.');
      return;
    }
    if (isRecording) return;
    try {
      const headers = getAuthHeaders({ useBearer: false }) || {};
      const apiKey = headers['X-API-Key'];
      if (!apiKey) throw new Error('Missing X-API-Key for websocket voice stream');

      const ws = new WebSocket(makeWsUrl(API_BASE_URL, '/api/v1/agentic-shop/voice/stream', apiKey));
      wsRef.current = ws;
      recordedChunksRef.current = [];
      setPartialTranscript('');

      ws.onmessage = (event) => {
        try {
          const payload = JSON.parse(event.data || '{}');
          const eventType = String(payload.event || '');
          if (eventType === 'partial') {
            setPartialTranscript(String(payload.transcript || ''));
          } else if (eventType === 'final') {
            const transcript = String(payload.transcript || '').trim();
            if (transcript) {
              setInput(transcript);
              setPartialTranscript('');
            }
          } else if (eventType === 'error') {
            toast.error(String(payload.message || 'Voice stream error'));
          }
        } catch {
          // Ignore malformed messages.
        }
      };

      ws.onerror = () => {
        toast.error('Voice websocket encountered an error.');
      };

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const recorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = (event) => {
        if (!event.data || event.data.size === 0) return;
        recordedChunksRef.current.push(event.data);
        if (ws.readyState === WebSocket.OPEN) {
          event.data.arrayBuffer().then((buffer) => {
            if (ws.readyState === WebSocket.OPEN) {
              ws.send(buffer);
            }
          });
        }
      };

      recorder.start(250);
      setIsRecording(true);
    } catch (error) {
      toast.error(String(error?.message || error));
      setIsRecording(false);
    }
  };

  const stopVoice = async () => {
    if (!isRecording) return;
    setIsRecording(false);

    try {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ event: 'stop' }));
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
      setTimeout(() => {
        if (wsRef.current && wsRef.current.readyState <= WebSocket.OPEN) {
          wsRef.current.close();
        }
      }, 400);
      await transcribeWithWhisper();
    } catch (error) {
      toast.error(String(error?.message || error));
    } finally {
      mediaRecorderRef.current = null;
      streamRef.current = null;
      wsRef.current = null;
    }
  };

  const visibleProducts = useMemo(
    () => products.slice(0, visibleCount),
    [products, visibleCount]
  );
  const groupedFacetOptions = useMemo(() => {
    const groups = {};
    for (const opt of Array.isArray(facetOptions) ? facetOptions : []) {
      if (!opt || typeof opt !== 'object') continue;
      const key = String(opt.group || 'other');
      if (!groups[key]) groups[key] = [];
      groups[key].push(opt);
    }
    return groups;
  }, [facetOptions]);
  const canLoadMore = visibleCount < products.length;
  const handleLoadMore = () => {
    setVisibleCount((prev) => Math.min(prev + INITIAL_VISIBLE_PRODUCTS, products.length));
  };

  useLayoutEffect(() => {
    const container = chatScrollRef.current;
    if (!container) return undefined;
    const raf = window.requestAnimationFrame(() => {
      container.scrollTop = container.scrollHeight;
    });
    return () => window.cancelAnimationFrame(raf);
  }, [chatHistory, loading, loadingStatus, partialTranscript]);

  useEffect(() => {
    if (!loading) {
      setLoadingStatus(LOADING_STATUS_MESSAGES[0]);
      return undefined;
    }
    let step = 0;
    setLoadingStatus(LOADING_STATUS_MESSAGES[0]);
    const timer = window.setInterval(() => {
      step = (step + 1) % LOADING_STATUS_MESSAGES.length;
      setLoadingStatus(LOADING_STATUS_MESSAGES[step]);
    }, 2200);
    return () => window.clearInterval(timer);
  }, [loading]);

  useEffect(() => {
    loadSecondPassSettings();
    loadAgentSetup();
    loadConnectorStatus();
    loadMcpServers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="container-fluid px-2 px-md-3 px-xl-4 py-0 agentic-shop-page">
      <div className="row gx-3 gy-0 agentic-main-row">
        <div className="col-12 col-xl-8 order-2 order-xl-1 agentic-results-col">
          <div className="card border-0 shadow-sm">
            <div className="card-header bg-white border-0 pt-3">
              <div className="d-flex flex-column flex-lg-row align-items-lg-center justify-content-between gap-2 mb-2">
                <div className="d-flex align-items-center gap-2">
                  <div className="rounded-circle bg-dark text-white d-inline-flex align-items-center justify-content-center" style={{ width: 30, height: 30 }}>
                    <Sparkles size={14} />
                  </div>
                  <div>
                    <div className="fw-semibold">Agentic Shop</div>
                    <div className="small text-muted">Conversational layer over OpenSearch hybrid/semantic search</div>
                  </div>
                </div>
                <div className="d-flex flex-wrap align-items-center gap-2">
                  <span className="badge text-bg-dark">Cart {cartItems.length}</span>
                  <span className={`badge ${memoryReady === true ? 'text-bg-success' : memoryReady === false ? 'text-bg-danger' : 'text-bg-secondary'}`}>
                    Memory {memoryReady === true ? 'Ready' : memoryReady === false ? 'Not Ready' : 'Unknown'}
                  </span>
                </div>
              </div>
              {partialTranscript && (
                <div className="alert alert-info mb-2 py-2">
                  <strong>Live transcript:</strong> {partialTranscript}
                </div>
              )}
              <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
                <h6 className="mb-0 d-flex align-items-center gap-2">
                  Product Results
                  {loading && (
                    <span className="badge text-bg-primary d-inline-flex align-items-center gap-1 agentic-results-working-badge">
                      <span className="spinner-border spinner-border-sm" style={{ width: '0.65rem', height: '0.65rem' }} aria-hidden="true" />
                      Working
                    </span>
                  )}
                </h6>
                <div className="d-flex align-items-center gap-2 small">
                  <label htmlFor="second-pass-engine" className="text-muted mb-0">2nd pass</label>
                  <select
                    id="second-pass-engine"
                    className="form-select form-select-sm"
                    style={{ width: '130px' }}
                    value={secondPassEngine}
                    onChange={(e) => updateSecondPassEngine(e.target.value)}
                    disabled={engineSaving}
                  >
                    <option value="inbuilt">Inbuilt</option>
                    <option value="ai">AI Engine</option>
                  </select>
                  {secondPassEngine === 'ai' && !aiEngineConfigured && (
                    <span className="badge text-bg-warning">AI key missing in settings</span>
                  )}
                  <div className="text-muted">
                    Showing {Math.min(visibleCount, products.length)} of {Math.max(totalResults, products.length)} results
                  </div>
                </div>
              </div>
              <div className="mt-2 d-flex flex-wrap align-items-center gap-2">
                <button
                  type="button"
                  className={`btn btn-sm ${agentSetupOpen ? 'btn-dark' : 'btn-outline-dark'}`}
                  onClick={() => setAgentSetupOpen((prev) => !prev)}
                >
                  {agentSetupOpen ? 'Hide Agent Setup' : 'Manage Orchestrator Agents'}
                </button>
                <span className="small text-muted">
                  Toggle which Agentic Shop agents are active for orchestration.
                </span>
              </div>
              {agentSetupOpen && (
                <div className="mt-2 border rounded-3 p-2 bg-light-subtle">
                  <div className="row g-2">
                    {Object.keys(agentSetup).sort().map((agentName) => (
                      <div key={agentName} className="col-12 col-md-6 col-xl-4">
                        <div className="form-check form-switch">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id={`agent-toggle-${agentName}`}
                            checked={Boolean(agentSetup[agentName])}
                            onChange={(e) => toggleAgent(agentName, e.target.checked)}
                            disabled={agentSetupSaving}
                          />
                          <label className="form-check-label small" htmlFor={`agent-toggle-${agentName}`}>
                            {agentName}
                          </label>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-2">
                    <button
                      type="button"
                      className="btn btn-sm btn-dark"
                      onClick={saveAgentSetup}
                      disabled={agentSetupSaving}
                    >
                      {agentSetupSaving ? 'Saving...' : 'Save Agent Setup'}
                    </button>
                  </div>
                </div>
              )}
              <div className="mt-2 border rounded-3 p-2 bg-white">
                <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
                  <div className="small fw-semibold">MCP / Connector Status</div>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary py-0 px-2"
                    onClick={loadConnectorStatus}
                    disabled={connectorStatusLoading}
                  >
                    {connectorStatusLoading ? 'Checking...' : 'Refresh'}
                  </button>
                </div>
                {connectorStatus.length === 0 ? (
                  <div className="small text-muted mt-2">No connector status available.</div>
                ) : (
                  <div className="d-flex flex-column gap-1 mt-2">
                    {connectorStatus.map((item, idx) => (
                      <div
                        key={`${String(item?.key || idx)}-${idx}`}
                        className="d-flex flex-wrap align-items-center justify-content-between gap-2 small"
                      >
                        <div className="d-flex flex-column gap-1">
                          <div>{String(item?.label || item?.key || 'Connector')}</div>
                          {!item?.configured && String(item?.key || '') === 'agent_registry' && (
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-dark py-0 px-2 align-self-start"
                              onClick={() => setAgentSetupOpen(true)}
                            >
                              Open Agent Setup
                            </button>
                          )}
                          {!item?.configured && ['opensearch', 'openai_llm'].includes(String(item?.key || '')) && (
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-dark py-0 px-2 align-self-start"
                              onClick={() => navigate('/open-search/ecommerce')}
                            >
                              Open OpenSearch Settings
                            </button>
                          )}
                        </div>
                        <span className={`badge ${item?.configured ? 'text-bg-success' : 'text-bg-warning'}`}>
                          {item?.configured ? 'Configured' : 'Missing'}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
                {externalMcpNote && (
                  <div className="small text-muted mt-2">{externalMcpNote}</div>
                )}
              </div>
              <div className="mt-2 border rounded-3 p-2 bg-light">
                <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
                  <div className="small fw-semibold">MCP Server Manager</div>
                  <div className="d-flex flex-wrap gap-1">
                    <button
                      type="button"
                      className={`btn btn-sm ${mcpPanelOpen ? 'btn-dark' : 'btn-outline-dark'} py-0 px-2`}
                      onClick={() => setMcpPanelOpen((prev) => !prev)}
                    >
                      {mcpPanelOpen ? 'Hide Form' : 'Add MCP Server'}
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary py-0 px-2"
                      onClick={loadMcpServers}
                    >
                      Refresh
                    </button>
                  </div>
                </div>
                {mcpPanelOpen && (
                  <div className="row g-2 mt-1">
                    <div className="col-12 col-md-6">
                      <label className="form-label small mb-1">Name</label>
                      <input
                        className="form-control form-control-sm"
                        value={mcpForm.name}
                        onChange={(e) => setMcpForm((prev) => ({ ...prev, name: e.target.value }))}
                        placeholder="Filesystem MCP"
                      />
                    </div>
                    <div className="col-12 col-md-6">
                      <label className="form-label small mb-1">Install Method</label>
                      <select
                        className="form-select form-select-sm"
                        value={mcpForm.install_method}
                        onChange={(e) => setMcpForm((prev) => ({ ...prev, install_method: e.target.value }))}
                      >
                        <option value="npm_global">npm -g</option>
                        <option value="pip">pip</option>
                      </select>
                    </div>
                    <div className="col-12 col-md-6">
                      <label className="form-label small mb-1">Package Name</label>
                      <input
                        className="form-control form-control-sm"
                        value={mcpForm.package_name}
                        onChange={(e) => setMcpForm((prev) => ({ ...prev, package_name: e.target.value }))}
                        placeholder="@modelcontextprotocol/server-filesystem"
                      />
                    </div>
                    <div className="col-12 col-md-6">
                      <label className="form-label small mb-1">Command</label>
                      <input
                        className="form-control form-control-sm"
                        value={mcpForm.command}
                        onChange={(e) => setMcpForm((prev) => ({ ...prev, command: e.target.value }))}
                        placeholder="mcp-server-filesystem"
                      />
                    </div>
                    <div className="col-12">
                      <label className="form-label small mb-1">Args (space-separated)</label>
                      <input
                        className="form-control form-control-sm"
                        value={mcpForm.args_text}
                        onChange={(e) => setMcpForm((prev) => ({ ...prev, args_text: e.target.value }))}
                        placeholder="/var/data --readonly"
                      />
                    </div>
                    <div className="col-12">
                      <label className="form-label small mb-1">Env JSON (optional)</label>
                      <textarea
                        className="form-control form-control-sm"
                        rows={3}
                        value={mcpForm.env_text}
                        onChange={(e) => setMcpForm((prev) => ({ ...prev, env_text: e.target.value }))}
                        placeholder='{"API_KEY":"..."}'
                      />
                    </div>
                    <div className="col-12 d-flex flex-wrap align-items-center gap-2">
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="mcp-enabled"
                          checked={Boolean(mcpForm.enabled)}
                          onChange={(e) => setMcpForm((prev) => ({ ...prev, enabled: e.target.checked }))}
                        />
                        <label className="form-check-label small" htmlFor="mcp-enabled">Enabled</label>
                      </div>
                      <button
                        type="button"
                        className="btn btn-sm btn-dark py-0 px-2"
                        onClick={saveMcpServer}
                        disabled={mcpSaving}
                      >
                        {mcpSaving ? 'Saving...' : (mcpForm.id ? 'Update' : 'Save')}
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary py-0 px-2"
                        onClick={clearMcpForm}
                        disabled={mcpSaving}
                      >
                        Clear
                      </button>
                    </div>
                  </div>
                )}
                <div className="table-responsive mt-2">
                  <table className="table table-sm table-bordered align-middle mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Name</th>
                        <th>Package</th>
                        <th>Command</th>
                        <th>Status</th>
                        <th style={{ width: '230px' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {mcpServers.length === 0 ? (
                        <tr>
                          <td colSpan={5} className="small text-muted">No MCP servers configured yet.</td>
                        </tr>
                      ) : (
                        mcpServers.map((server) => {
                          const id = String(server?.id || '');
                          const installBusy = Boolean(mcpBusyById[`install:${id}`]);
                          const testBusy = Boolean(mcpBusyById[`test:${id}`]);
                          const deleteBusy = Boolean(mcpBusyById[`delete:${id}`]);
                          const lastInstallOk = server?.last_install_ok;
                          const lastTestOk = server?.last_test_ok;
                          return (
                            <tr key={id}>
                              <td className="small">{String(server?.name || '-')}</td>
                              <td className="small">{String(server?.package_name || '-')}</td>
                              <td className="small">{String(server?.command || '-')}</td>
                              <td className="small">
                                <div className="d-flex flex-column gap-1">
                                  <span className={`badge ${lastInstallOk === true ? 'text-bg-success' : lastInstallOk === false ? 'text-bg-danger' : 'text-bg-secondary'}`}>
                                    Install: {lastInstallOk === true ? 'OK' : lastInstallOk === false ? 'Failed' : 'N/A'}
                                  </span>
                                  <span className={`badge ${lastTestOk === true ? 'text-bg-success' : lastTestOk === false ? 'text-bg-danger' : 'text-bg-secondary'}`}>
                                    Test: {lastTestOk === true ? 'OK' : lastTestOk === false ? 'Failed' : 'N/A'}
                                  </span>
                                </div>
                              </td>
                              <td>
                                <div className="d-flex flex-wrap gap-1">
                                  <button type="button" className="btn btn-sm btn-outline-dark py-0 px-2" onClick={() => editMcpServer(server)}>
                                    Edit
                                  </button>
                                  <button type="button" className="btn btn-sm btn-outline-primary py-0 px-2" onClick={() => installMcpServer(id)} disabled={installBusy}>
                                    {installBusy ? 'Installing...' : 'Install'}
                                  </button>
                                  <button type="button" className="btn btn-sm btn-outline-success py-0 px-2" onClick={() => testMcpServer(id)} disabled={testBusy}>
                                    {testBusy ? 'Testing...' : 'Test'}
                                  </button>
                                  <button type="button" className="btn btn-sm btn-outline-danger py-0 px-2" onClick={() => deleteMcpServer(id)} disabled={deleteBusy}>
                                    {deleteBusy ? 'Removing...' : 'Remove'}
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
              {secondPassApplied && (
                <div className="small text-info mt-1">
                  Auto-refined using facets: {Object.entries(secondPassFilters).map(([k, v]) => `${k}=${v}`).join(', ')}
                </div>
              )}
              {canLoadMore && (
                <div className="mt-2">
                  <button type="button" className="btn btn-sm btn-outline-dark" onClick={handleLoadMore}>
                    Load More
                  </button>
                </div>
              )}
              {Object.keys(filtersApplied).length > 0 && (
                <div className="d-flex flex-wrap align-items-center gap-1 mt-2">
                  {Object.entries(filtersApplied).map(([k, v]) => (
                    <button
                      key={k}
                      type="button"
                      className="btn btn-sm btn-outline-secondary py-0 px-2"
                      onClick={() => removeSingleFilter(k)}
                      title={`Remove ${k} filter`}
                      disabled={loading}
                    >
                      {k}: {v} ×
                    </button>
                  ))}
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger py-0 px-2"
                    onClick={clearAllFilters}
                    disabled={loading}
                  >
                    Clear All
                  </button>
                </div>
              )}
              <div className="mt-3 border rounded-3 p-2 bg-light-subtle">
                <div className="small fw-semibold mb-2">Live Facet Filters</div>
                {Object.keys(groupedFacetOptions).length === 0 ? (
                  <div className="small text-muted">
                    No facet values available for further narrowing on current result set.
                  </div>
                ) : (
                  <div className="d-flex flex-column gap-2">
                    {Object.entries(groupedFacetOptions).map(([group, options]) => (
                      <div key={group}>
                        <div className="small text-muted mb-1 text-capitalize">{group.replace('attribute:', '')}</div>
                        <div className="d-flex flex-wrap gap-1">
                          {options.slice(0, 10).map((opt, idx) => (
                            <button
                              key={`${group}-${opt.filter_key || 'k'}-${opt.value || idx}-${idx}`}
                              type="button"
                              className="btn btn-sm btn-outline-dark py-0 px-2"
                              onClick={() =>
                                handleFacetOptionClick(opt, {
                                  contextQuery: facetContext.query,
                                  contextFilters: facetContext.filters,
                                })
                              }
                              disabled={loading}
                              title={opt.prompt || ''}
                            >
                              {opt.label || `${opt.filter_key}: ${opt.value}`}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="card-body">
              {loading && (
                <div
                  className="alert alert-primary border-0 d-flex align-items-center gap-3 mb-3 py-2 agentic-results-working-banner"
                  role="status"
                  aria-live="polite"
                  aria-busy="true"
                >
                  <div className="spinner-border spinner-border-sm text-primary" aria-hidden="true" />
                  <div>
                    <div className="fw-semibold small mb-0">Working on your request</div>
                    <div className="small text-primary-emphasis mb-0">{loadingStatus}</div>
                  </div>
                </div>
              )}
              {comparison && Array.isArray(comparison.rows) && comparison.rows.length > 0 && (
                <div className="table-responsive mb-3">
                  <table className="table table-sm table-bordered align-middle">
                    <thead className="table-light">
                      <tr>
                        <th style={{ width: '22%' }}>Field</th>
                        <th>{productDisplayText(comparison.left_title, 'Product 1')}</th>
                        <th>{productDisplayText(comparison.right_title, 'Product 2')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {comparison.rows.map((row, idx) => (
                        <tr key={`${row.field}-${idx}`}>
                          <td className="fw-semibold">{productDisplayText(row.field)}</td>
                          <td>{productDisplayText(row.left)}</td>
                          <td>{productDisplayText(row.right)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {loading && products.length === 0 ? (
                <div className="agentic-results-skeleton row g-3" aria-hidden="true">
                  {[0, 1, 2].map((idx) => (
                    <div key={`skeleton-${idx}`} className="col-12 col-sm-6 col-lg-4">
                      <div className="card border-0 shadow-sm h-100 placeholder-glow">
                        <div className="placeholder col-12 rounded-0" style={{ height: 190 }} />
                        <div className="card-body">
                          <div className="placeholder col-10 mb-2" />
                          <div className="placeholder col-6 mb-2" />
                          <div className="placeholder col-8 mb-3" />
                          <div className="placeholder col-12" style={{ height: 32 }} />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : products.length === 0 ? (
                <div className="text-muted small">No products yet. Submit a query to start.</div>
              ) : (
                <div className={`row g-3 ${loading ? 'agentic-results-grid-loading' : ''}`}>
                  {visibleProducts.map((product, idx) => (
                    <ProductCard
                      key={`${product.item_id || product.part_number || idx}`}
                      product={product}
                      onAddToCart={addToCart}
                    />
                  ))}
                </div>
              )}
              {canLoadMore && (
                <div className="d-flex justify-content-center mt-3">
                  <button type="button" className="btn btn-outline-dark" onClick={handleLoadMore}>
                    Load More
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="col-12 col-xl-4 order-1 order-xl-2 agentic-chat-col">
          <div className="card border-0 shadow-sm h-100 agentic-chat-panel">
            <div className="card-header bg-white border-0 pt-3">
              <h6 className="mb-0 d-flex align-items-center gap-2 flex-wrap">
                <MessageSquare size={16} />
                Conversation
                {loading && (
                  <span className="badge text-bg-primary d-inline-flex align-items-center gap-1">
                    <span className="spinner-border spinner-border-sm" style={{ width: '0.65rem', height: '0.65rem' }} aria-hidden="true" />
                    Working
                  </span>
                )}
              </h6>
            </div>
            <div className="card-body agentic-chat-body">
              <div
                ref={chatScrollRef}
                className="border rounded p-2 agentic-chat-scroll"
              >
                {chatHistory.length === 0 ? (
                  <div className="text-muted small">Ask for products, refine filters, or compare previous results.</div>
                ) : (
                  <div className="d-flex flex-column gap-2">
                    {chatHistory.map((item, idx) => (
                      <div key={`${item.role}-${idx}`} className={`d-flex ${item.role === 'user' ? 'justify-content-end' : 'justify-content-start'}`}>
                        <div className={`rounded-3 px-3 py-2 ${item.role === 'user' ? 'bg-dark text-white' : 'bg-light text-dark'}`} style={{ maxWidth: '90%' }}>
                          <div className="small d-flex align-items-center gap-1 mb-1">
                            {item.role === 'user' ? <User size={13} /> : <Bot size={13} />}
                            {item.role === 'user' ? 'You' : 'Assistant'}
                          </div>
                          <div className="small chat-message-content">
                            {item.role === 'assistant' ? renderFormattedMessage(item.text) : <span style={{ whiteSpace: 'pre-wrap' }}>{item.text}</span>}
                          </div>
                          {item.role === 'assistant' && item.agentCalled && (
                            <div className="small mt-2">
                              <span className="badge text-bg-secondary">Agent: {item.agentCalled}</span>
                              {item.secondPassApplied && (
                                <span className="badge text-bg-info ms-1">Auto-refined (2nd pass)</span>
                              )}
                            </div>
                          )}
                          {item.role === 'assistant' && Array.isArray(item.agentMissing) && item.agentMissing.length > 0 && (
                            <div className="alert alert-warning mt-2 mb-0 py-2 px-2 small">
                              Setup required: {item.agentMissing.join(', ')}
                            </div>
                          )}
                          {item.role === 'assistant' && Array.isArray(item.products) && item.products.length > 0 && (
                            <div className="mt-2 pt-2 border-top d-flex flex-column gap-2">
                              {item.products.map((prod, prodIdx) => (
                                <div
                                  key={`${prod.item_id || prod.part_number || prodIdx}`}
                                  className="d-flex flex-wrap align-items-center justify-content-between gap-2 bg-white rounded-2 px-2 py-1 border"
                                >
                                  <div className="small text-dark" style={{ maxWidth: '70%', overflowWrap: 'anywhere' }}>
                                    {productDisplayText(prod.short_desc || prod.part_number, 'Product')}
                                  </div>
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-dark chat-icon-btn"
                                    onClick={() => addToCart(prod)}
                                    aria-label="Add to cart"
                                    title="Add to cart"
                                  >
                                    <i className="fa-solid fa-cart-plus" aria-hidden="true" />
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                          {item.role === 'assistant' && Array.isArray(item.followUps) && item.followUps.length > 0 && (
                            <div className="mt-2 d-flex flex-wrap gap-1">
                              {item.followUps.map((q, qIdx) => (
                                <button
                                  key={`${q}-${qIdx}`}
                                  type="button"
                                  className="btn btn-sm btn-outline-secondary py-0 px-2"
                                  onClick={() => handleFollowUpClick(q, { intent: item.intent })}
                                >
                                  {q}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                    {loading && <AgenticWorkingIndicator message={loadingStatus} />}
                    <div ref={chatBottomRef} />
                  </div>
                )}
                {loading && chatHistory.length === 0 && (
                  <div className="mt-2">
                    <AgenticWorkingIndicator message={loadingStatus} />
                  </div>
                )}
              </div>
              {followUps.length > 0 && (
                <div className="d-flex flex-wrap gap-2">
                  {followUps.map((q, idx) => (
                    <button
                      key={`${q}-${idx}`}
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => handleFollowUpClick(q)}
                      disabled={loading}
                    >
                      {q}
                    </button>
                  ))}
                </div>
              )}
              <div className={`border rounded-3 p-2 bg-white shadow-sm agentic-chat-composer ${loading ? 'agentic-chat-composer-busy' : ''}`}>
                {loading && (
                  <div
                    className="small text-primary fw-semibold d-flex align-items-center gap-2 mb-2 agentic-composer-status"
                    role="status"
                    aria-live="polite"
                  >
                    <span className="spinner-border spinner-border-sm text-primary" aria-hidden="true" />
                    {loadingStatus}
                  </div>
                )}
                <textarea
                  className="form-control border-0 shadow-none"
                  rows={4}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  disabled={loading}
                  aria-busy={loading}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      if (!loading && input.trim()) {
                        sendChat(input);
                      }
                    }
                  }}
                  placeholder="Ask for products, e.g. 'chemical resistant gloves', then refine with 'only nitrile'"
                />
                <div className="d-flex align-items-center justify-content-between pt-2 mt-1 border-top">
                  <div className={`small ${loading ? 'text-primary fw-semibold' : 'text-muted'}`}>
                    {loading ? 'Please wait while we process your request...' : 'Press Enter to send, Shift+Enter for new line'}
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <button
                      type="button"
                      className={`btn btn-sm ${isRecording ? 'btn-danger' : 'btn-outline-secondary'} chat-icon-btn`}
                      onClick={isRecording ? stopVoice : startVoice}
                      disabled={loading}
                      aria-label={isRecording ? 'Stop voice input' : 'Start voice input'}
                      title={isRecording ? 'Stop voice input' : 'Start voice input'}
                    >
                      <i className={`fa-solid ${isRecording ? 'fa-microphone-slash' : 'fa-microphone'}`} aria-hidden="true" />
                    </button>
                    <button
                      type="button"
                      className="btn btn-dark btn-sm chat-icon-btn"
                      onClick={() => sendChat(input)}
                      disabled={loading || !input.trim()}
                      aria-label={loading ? 'Sending' : 'Send message'}
                      title={loading ? 'Sending' : 'Send message'}
                    >
                      {loading ? (
                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                      ) : (
                        <i className="fa-solid fa-paper-plane" aria-hidden="true" />
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <style>{`
        .agentic-shop-page .agentic-main-row {
          align-items: flex-start;
        }
        .agentic-shop-page .agentic-chat-col {
          display: flex;
        }
        .agentic-shop-page .agentic-chat-panel {
          position: sticky;
          top: 0;
          height: 100vh;
          overflow: hidden;
          display: flex;
          flex-direction: column;
          width: 100%;
        }
        .agentic-shop-page .agentic-chat-body {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
          min-height: 0;
          overflow: hidden;
        }
        .agentic-shop-page .agentic-chat-scroll {
          flex: 1 1 auto;
          min-height: 260px;
          max-height: none;
          overflow-y: auto;
        }
        .agentic-shop-page .agentic-chat-composer {
          flex: 0 0 auto;
        }
        .agentic-shop-page .agentic-chat-composer-busy {
          border-color: #b6d4fe !important;
          box-shadow: 0 0 0 0.15rem rgba(13, 110, 253, 0.15) !important;
        }
        .agentic-shop-page .agentic-working-message {
          animation: agentic-pulse 1.4s ease-in-out infinite;
        }
        .agentic-shop-page .agentic-results-working-banner {
          background: linear-gradient(90deg, #e7f1ff 0%, #f8fbff 50%, #e7f1ff 100%);
          background-size: 200% 100%;
          animation: agentic-shimmer 2s linear infinite;
        }
        .agentic-shop-page .agentic-results-grid-loading {
          opacity: 0.55;
          pointer-events: none;
          transition: opacity 0.2s ease;
        }
        @keyframes agentic-pulse {
          0%, 100% { opacity: 0.65; }
          50% { opacity: 1; }
        }
        @keyframes agentic-shimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
        .agentic-shop-page .ecom-product-card {
          border: 1px solid #e9eef5 !important;
          border-radius: 0.9rem;
          overflow: hidden;
          transition: transform 0.2s ease, box-shadow 0.2s ease;
          display: flex;
          flex-direction: column;
        }
        .agentic-shop-page .ecom-product-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 12px 24px rgba(15, 23, 42, 0.12) !important;
        }
        .agentic-shop-page .ecom-product-image-wrap {
          position: relative;
          width: 100%;
          height: 190px;
          background: #f8fafc;
          display: flex;
          align-items: center;
          justify-content: center;
          border-bottom: 1px solid #eef2f7;
        }
        .agentic-shop-page .ecom-product-image {
          width: 100%;
          height: 100%;
          object-fit: contain;
          object-position: center;
          background: #f8fafc;
        }
        .agentic-shop-page .ecom-product-image-fallback {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          background: repeating-linear-gradient(
            45deg,
            #f1f5f9,
            #f1f5f9 12px,
            #e2e8f0 12px,
            #e2e8f0 24px
          );
          color: #64748b;
          font-weight: 600;
          font-size: 0.78rem;
          letter-spacing: 0.03em;
          text-transform: uppercase;
        }
        .agentic-shop-page .ecom-score-badge {
          position: absolute;
          top: 10px;
          right: 10px;
          border: 1px solid #dce3ec;
          backdrop-filter: blur(2px);
        }
        .agentic-shop-page .ecom-product-title {
          line-height: 1.35;
          overflow-wrap: anywhere;
          word-break: break-word;
          white-space: normal;
        }
        .agentic-shop-page .ecom-attr-pill {
          font-weight: 500;
          max-width: 100%;
          white-space: normal;
          text-align: left;
        }
        .agentic-shop-page .chat-message-content .formatted-chat-message p,
        .agentic-shop-page .chat-message-content .formatted-chat-message li {
          line-height: 1.45;
        }
        .agentic-shop-page .chat-message-content .formatted-chat-message ul {
          margin-top: 0;
        }
        .agentic-shop-page .chat-icon-btn {
          width: 2rem;
          height: 2rem;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          border-radius: 999px;
          padding: 0;
          line-height: 1;
        }
        .agentic-shop-page .chat-icon-btn i {
          font-size: 0.85rem;
        }
        @media (min-width: 1200px) {
          .agentic-shop-page .agentic-results-col {
            flex: 0 0 70%;
            max-width: 70%;
          }
          .agentic-shop-page .agentic-chat-col {
            flex: 0 0 30%;
            max-width: 30%;
          }
        }
        @media (max-width: 1199.98px) {
          .agentic-shop-page .agentic-chat-col {
            display: block;
          }
          .agentic-shop-page .agentic-chat-panel {
            position: static;
            height: auto;
          }
        }
        @media (max-width: 575.98px) {
          .agentic-shop-page .ecom-product-image-wrap {
            height: 155px;
          }
        }
      `}</style>
    </div>
  );
};

export default AgenticShop;
